import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { FileText, FileSpreadsheet, Image as ImageIcon, Upload, Trash2, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { mget, mdel, errMsg, fmtTs } from "@/manager/lib";
import { Card, Pill, Empty, goldBtn } from "@/manager/ui";

const CATS = { presupuesto: "Presupuesto", menu: "Menú", contrato: "Contrato", otro: "Otro" };
const ACCEPT = ".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.xls,.xlsx";
const icon = (t) => (t.startsWith("image/") ? ImageIcon : /sheet|excel/.test(t) ? FileSpreadsheet : FileText);
const kb = (n) => (n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

function DocRow({ d, onDelete }) {
  const Icon = icon(d.content_type);
  const open = async () => {
    try {
      const r = await api.get(`/manager/documents/${d.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = Object.assign(document.createElement("a"), { href: url, target: "_blank", rel: "noopener" });
      if (!/pdf|image/.test(d.content_type)) a.download = d.filename;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="flex flex-wrap items-center gap-3 py-3 border-t border-[#f0ece4] first:border-t-0" data-testid={`doc-${d.id}`}>
      <Icon size={18} className="text-[#BBA77D]" />
      <button onClick={open} className="flex-1 text-left text-sm font-medium hover:underline truncate" data-testid={`doc-open-${d.id}`}>{d.filename}</button>
      <Pill className="bg-[#F7F5F0] text-[#101B28] border-[#e6e1d6]">{CATS[d.category]}</Pill>
      <span className="text-xs text-[#6b6a66]">{kb(d.size)} · {fmtTs(d.created_at)}</span>
      <button onClick={open} className="text-[#6b6a66] hover:text-[#101B28]" aria-label="Descargar" data-testid={`doc-download-${d.id}`}><Download size={15} /></button>
      <button onClick={() => onDelete(d)} className="text-rose-700" aria-label="Eliminar" data-testid={`doc-delete-${d.id}`}><Trash2 size={15} /></button>
    </div>
  );
}

export default function DocumentsTab({ ev }) {
  const [docs, setDocs] = useState([]);
  const [cat, setCat] = useState("presupuesto");
  const [busy, setBusy] = useState(false);
  const input = useRef(null);
  const load = () => mget(`/events/${ev.id}/documents`).then(setDocs).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, [ev.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const upload = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("category", cat);
      try { await api.post(`/manager/events/${ev.id}/documents`, fd); toast.success(`${f.name} subido`); }
      catch (e) { toast.error(`${f.name}: ${errMsg(e)}`); }
    }
    setBusy(false);
    input.current.value = "";
    load();
  };
  const remove = async (d) => {
    if (!window.confirm(`¿Eliminar «${d.filename}»?`)) return;
    try { await mdel(`/documents/${d.id}`); toast.success("Documento eliminado"); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-5" data-testid="ev-documents">
      <Card className="p-5 flex flex-wrap items-center gap-3">
        <Select value={cat} onValueChange={setCat}>
          <SelectTrigger className="w-44" data-testid="doc-category"><SelectValue /></SelectTrigger>
          <SelectContent>{Object.entries(CATS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
        </Select>
        <input ref={input} type="file" multiple accept={ACCEPT} className="hidden" onChange={(e) => upload(e.target.files)} data-testid="doc-file-input" />
        <Button className={goldBtn} disabled={busy} onClick={() => input.current.click()} data-testid="doc-upload-btn"><Upload size={14} /> {busy ? "Subiendo…" : "Adjuntar archivo"}</Button>
        <p className="text-xs text-[#6b6a66]">PDF, fotos (JPG, PNG, WEBP), Word o Excel · máx. 15 MB</p>
      </Card>
      <Card className="p-5">
        {docs.map((d) => <DocRow key={d.id} d={d} onDelete={remove} />)}
        {!docs.length && <Empty>Aún no hay presupuestos, menús ni contratos adjuntos.</Empty>}
      </Card>
    </div>
  );
}
